'use client';

import React, { useState } from 'react';
import Header from '@/components/common/Header';

interface IndustryResource {
  id: string;
  title: string;
  category: string;
  description: string;
  lastUpdated: string;
  downloadLink?: string;
  externalLink?: string;
}

export default function IndustryInformationContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    'All Categories',
    'Safety Standards',
    'Building Codes',
    'Industry Regulations',
    'Best Practices',
    'Technical Guidelines',
    'Compliance Requirements'
  ];

  const industryResources: IndustryResource[] = [
    {
      id: '1',
      title: 'ASME A17.1 Safety Code for Elevators and Escalators',
      category: 'Safety Standards',
      description: 'Comprehensive safety requirements covering the design, construction, operation, inspection, testing, maintenance, alteration, and repair of elevators and escalators.',
      lastUpdated: '2024',
      externalLink: 'https://www.asme.org/codes-standards/find-codes-standards/a17-1-safety-code-elevators-escalators'
    },
    {
      id: '2',
      title: 'International Building Code (IBC) Elevator Requirements',
      category: 'Building Codes',
      description: 'Building code provisions related to elevator installation, accessibility, fire safety, and structural requirements.',
      lastUpdated: '2024',
      externalLink: 'https://codes.iccsafe.org/content/IBC2024P1'
    },
    {
      id: '3',
      title: 'ADA Accessibility Guidelines for Elevators',
      category: 'Compliance Requirements',
      description: 'Americans with Disabilities Act standards ensuring elevator accessibility for individuals with disabilities.',
      lastUpdated: '2023',
      externalLink: 'https://www.access-board.gov/ada/'
    },
    {
      id: '4',
      title: 'NFPA 70 National Electrical Code (NEC) - Elevator Provisions',
      category: 'Technical Guidelines',
      description: 'Electrical safety standards applicable to elevator installations including wiring, grounding, and power requirements.',
      lastUpdated: '2023',
      externalLink: 'https://www.nfpa.org/codes-and-standards/all-codes-and-standards/list-of-codes-and-standards/detail?code=70'
    },
    {
      id: '5',
      title: 'Preventive Maintenance Best Practices',
      category: 'Best Practices',
      description: 'Industry-recommended maintenance schedules, inspection procedures, and preventive care strategies to ensure elevator safety and reliability.',
      lastUpdated: '2024'
    },
    {
      id: '6',
      title: 'Emergency Operations and Evacuation Procedures',
      category: 'Safety Standards',
      description: 'Guidelines for emergency power, fire service operations, earthquake safety features, and passenger evacuation protocols.',
      lastUpdated: '2024'
    },
    {
      id: '7',
      title: 'Modernization and Retrofit Guidelines',
      category: 'Technical Guidelines',
      description: 'Standards and recommendations for upgrading existing elevator systems to meet current codes and improve performance.',
      lastUpdated: '2023'
    },
    {
      id: '8',
      title: 'OSHA Elevator Safety Regulations',
      category: 'Industry Regulations',
      description: 'Occupational Safety and Health Administration requirements for elevator construction, maintenance, and repair work.',
      lastUpdated: '2024',
      externalLink: 'https://www.osha.gov/elevator-safety'
    }
  ];

  const filteredResources = industryResources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           selectedCategory === 'All Categories' ||
                           resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Industry Information
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Stay current with elevator industry standards, safety codes, regulations, and best practices. 
              Access essential resources to ensure compliance and maintain the highest safety standards.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none bg-white"
              >
                {categories.map((category) => (
                  <option key={category} value={category.toLowerCase().replace(' ', '-')}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {filteredResources.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-gray-900 hover:shadow-lg transition-all duration-300"
                >
                  <div className="mb-4">
                    <span className="inline-block bg-gray-900 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {resource.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {resource.description}
                  </p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <span className="text-sm text-gray-500">
                      Updated: {resource.lastUpdated}
                    </span>
                    {resource.externalLink && (
                      <a
                        href={resource.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-gray-900 hover:text-gray-600 transition-colors"
                      >
                        View Resource →
                      </a>
                    )}
                    {resource.downloadLink && (
                      <a
                        href={resource.downloadLink}
                        className="text-sm font-semibold text-gray-900 hover:text-gray-600 transition-colors"
                      >
                        Download →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No resources found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Important Notice Section */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-2 border-gray-900 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Important Notice
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              The information provided here is for reference purposes and represents general industry standards. 
              Always verify compliance requirements with your local Authority Having Jurisdiction (AHJ) and consult 
              with certified professionals for specific applications.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Standards and regulations are regularly updated. Ensure you are referencing the most current versions 
              applicable to your jurisdiction and project requirements.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need Compliance Guidance?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Our team can help you navigate industry standards and ensure your project meets all requirements.
          </p>
          <a
            href="/service-support-request-form"
            className="inline-block bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
          >
            Contact Our Team
          </a>
        </div>
      </section>
    </div>
  );
}